import React from 'react';
import { Target, AlertTriangle, CheckCircle } from 'lucide-react';

interface SweetSpotHeatmapProps {
  impactX: number;
  impactY: number;
  sweetSpotDistance: number;
  powerTransfer: number;
}

export const SweetSpotHeatmap: React.FC<SweetSpotHeatmapProps> = ({
  impactX,
  impactY,
  sweetSpotDistance,
  powerTransfer
}) => {
  // Sweet spot is actually 10-15cm from bottom edge of bat (bat blade is ~60cm)
  // SVG blade goes from y=110 to y=310, so sweet spot should be at y=260 (50px from bottom)
  const sweetSpotCenter = { x: 0, y: -0.32 };
  const sweetSpotRadius = 0.03; // 3cm radius
  
  // Direct SVG coordinates for the sweet spot position
  const sweetSpotSvgX = 150; // Center of bat blade
  const sweetSpotSvgY = 260; // Lower third of blade (50px from bottom edge at y=310)
  
  // Scale for converting impact coordinates to SVG
  const scale = 400; // Scale factor for display
  const offsetX = 150; // Center X
  const offsetY = 200; // Offset Y
  const impactSvgX = impactX * scale + offsetX;
  const impactSvgY = -impactY * scale + offsetY;
  const radiusSvg = sweetSpotRadius * scale;
  
  const getImpactColor = () => {
    if (sweetSpotDistance <= sweetSpotRadius * 0.5) return '#22c55e'; // Perfect
    if (sweetSpotDistance <= sweetSpotRadius) return '#fbbf24'; // Good
    return '#ef4444'; // Poor
  };

  const getEfficiencyIcon = () => {
    if (powerTransfer >= 0.9) return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (powerTransfer >= 0.7) return <Target className="h-4 w-4 text-yellow-600" />;
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  };

  return (
    <div className="space-y-4">
      {/* Bat Outline with Sweet Spot */}
      <div className="mx-auto w-64 h-80 relative">
        <svg width="100%" height="100%" viewBox="0 0 300 320" className="border rounded-lg bg-gradient-to-b from-amber-50 to-amber-100">
          {/* Bat outline */}
          <g stroke="#92400e" strokeWidth="2" fill="#d97706" fillOpacity="0.1">
            {/* Handle */}
            <rect x="140" y="10" width="20" height="100" rx="10" />
            {/* Blade */}
            <rect x="120" y="110" width="60" height="200" rx="8" />
          </g>
          
          {/* Sweet spot zones (concentric circles) */}
          <g fillOpacity="0.2">
            {/* Outer zone */}
            <circle 
              cx={sweetSpotSvgX} 
              cy={sweetSpotSvgY} 
              r={radiusSvg * 2} 
              fill="#fbbf24" 
            />
            {/* Middle zone */}
            <circle 
              cx={sweetSpotSvgX} 
              cy={sweetSpotSvgY} 
              r={radiusSvg * 1.5} 
              fill="#f59e0b" 
            />
            {/* Sweet spot (perfect zone) */}
            <circle 
              cx={sweetSpotSvgX} 
              cy={sweetSpotSvgY} 
              r={radiusSvg} 
              fill="#22c55e" 
            />
          </g>
          
          {/* Sweet spot center marker */}
          <circle 
            cx={sweetSpotSvgX} 
            cy={sweetSpotSvgY} 
            r="3" 
            fill="#059669" 
            stroke="#065f46" 
            strokeWidth="1"
          />
          
          {/* Impact point */}
          <circle 
            cx={impactSvgX} 
            cy={impactSvgY} 
            r="8" 
            fill={getImpactColor()} 
            stroke="#ffffff" 
            strokeWidth="2"
          />
          
          {/* Connection line from sweet spot to impact */}
          <line 
            x1={sweetSpotSvgX} 
            y1={sweetSpotSvgY} 
            x2={impactSvgX} 
            y2={impactSvgY} 
            stroke="#6b7280" 
            strokeWidth="1" 
            strokeDasharray="3,3"
          />
          
          {/* Distance indicator */}
          <text 
            x={(sweetSpotSvgX + impactSvgX) / 2} 
            y={(sweetSpotSvgY + impactSvgY) / 2 - 5} 
            fontSize="10" 
            textAnchor="middle" 
            fill="#374151"
            className="font-mono"
          >
            {(sweetSpotDistance * 100).toFixed(1)}cm
          </text>
          
          {/* Zone labels */}
          <text x="20" y="30" fontSize="10" fill="#22c55e" className="font-semibold">PERFECT</text>
          <text x="20" y="45" fontSize="10" fill="#f59e0b" className="font-semibold">GOOD</text>
          <text x="20" y="60" fontSize="10" fill="#fbbf24" className="font-semibold">FAIR</text>
          
          {/* Coordinate grid (subtle) */}
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Impact Analysis */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Impact Quality</span>
          {getEfficiencyIcon()}
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Distance from sweet spot</p>
            <p className="font-medium">{(sweetSpotDistance * 100).toFixed(1)} cm</p>
          </div>
          <div>
            <p className="text-gray-600">Power efficiency</p>
            <p className="font-medium">{(powerTransfer * 100).toFixed(1)}%</p>
          </div>
        </div>
        
        <div className="space-y-1">
          <p className="text-gray-600 text-sm">Impact coordinates</p>
          <div className="grid grid-cols-2 gap-2 text-xs font-mono">
            <div>X: {(impactX * 100).toFixed(1)}cm</div>
            <div>Y: {(impactY * 100).toFixed(1)}cm</div>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className="bg-blue-50 p-3 rounded-lg">
        <p className="text-sm font-medium text-blue-900 mb-1">Recommendation</p>
        <p className="text-xs text-blue-800">
          {sweetSpotDistance <= sweetSpotRadius * 0.5 
            ? "Excellent contact! You hit the sweet spot perfectly."
            : sweetSpotDistance <= sweetSpotRadius
            ? "Good contact within sweet spot range. Try to center your impact more."
            : impactY > sweetSpotCenter.y
            ? "Impact too high on the bat. Aim lower towards the sweet spot (10-15cm from bottom edge)."
            : impactY < sweetSpotCenter.y - 0.15
            ? "Impact too close to the toe of the bat. Aim slightly higher."
            : Math.abs(impactX) > 0.02
            ? `Impact off-center. Aim more towards the ${impactX > 0 ? 'off-side' : 'leg-side'} edge.`
            : "Work on consistent sweet spot contact for maximum power transfer."
          }
        </p>
      </div>

      {/* Sweet Spot Tips */}
      <div className="text-xs text-gray-600 space-y-1">
        <p className="font-medium">Sweet Spot Tips:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Focus on watching the ball onto the bat</li>
          <li>Maintain good balance and head position</li>
          <li>Practice with marked sweet spot on bat</li>
          <li>Work on timing for consistent contact</li>
        </ul>
      </div>
    </div>
  );
};